import { useEffect, useState } from 'react';
import { ApiError } from '../../../shared/shared.api';
import { getDriver, type DriverDetail } from '../drivers.api';

export type DriverRecordState =
  { status: 'loading' } | { status: 'loaded'; driver: DriverDetail } | { status: 'forbidden' } | { status: 'not_found' } | { status: 'error' };

function toErrorState(err: unknown): DriverRecordState {
  if (err instanceof ApiError && err.status === 403) return { status: 'forbidden' };
  if (err instanceof ApiError && err.status === 404) return { status: 'not_found' };
  return { status: 'error' };
}

/** One driver, fetched by id and re-fetchable after a mutation commits. */
export function useDriverRecord(id: string) {
  const [state, setState] = useState<DriverRecordState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    getDriver(id)
      .then((driver) => {
        if (!cancelled) setState({ status: 'loaded', driver });
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  async function reload(): Promise<void> {
    try {
      setState({ status: 'loaded', driver: await getDriver(id) });
    } catch (err) {
      setState(toErrorState(err));
    }
  }

  return { state, reload };
}
